import React, { useState, useEffect, useRef } from 'react';
import Dashboard from './components/Dashboard';
import { useWebSocket } from './hooks/useApi';

const TABS = [
  { id: 'dashboard', label: '대시보드' },
  { id: 'bots', label: '봇' },
  { id: 'performance', label: '일별 성과' },
  { id: 'consensus', label: '합의' },
  { id: 'fundamental', label: '펀더멘털' },
  { id: 'trades', label: '거래' },
  { id: 'risk', label: '리스크' },
  { id: 'quant', label: '퀀트' },
  { id: 'persona', label: '페르소나' },
];

// ── KST 시간 유틸 ──
const getKSTDate = () => new Date(
  new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' })
);

const isKROpen = () => {
  const kst = getKSTDate();
  const day = kst.getDay();
  if (day === 0 || day === 6) return false;
  const time = kst.getHours() * 100 + kst.getMinutes();
  return time >= 900 && time < 1530;
};

const isUSOpen = () => {
  const kst = getKSTDate();
  const day = kst.getDay();
  if (day === 0 || day === 6) return false;
  const time = kst.getHours() * 100 + kst.getMinutes();
  return time >= 2230 || time < 500;
};

export default function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const { connected } = useWebSocket();
  const menuRef = useRef(null);

  // 매초 시간 갱신
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 드롭다운 외부 클릭 닫기
  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectTab = (id) => { setActiveTab(id); setMenuOpen(false); };

  const timeStr = now.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  const krOpen = isKROpen();
  const usOpen = isUSOpen();

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header — 슬림 1줄 */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-[1440px] mx-auto px-3 md:px-4 h-12 flex items-center justify-between">
          {/* 좌: 로고 + 5ETS */}
          <a
            href="https://nexai.co.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 no-underline flex-shrink-0"
          >
            <img src="/nexai-logo.png" alt="NexAI" className="h-7" />
            <span style={{ width: '1px', height: '18px', background: '#555', display: 'inline-block' }} />
            <span className="text-lg font-bold text-blue-400">5ETS</span>
            <span className="text-xs text-gray-500 hidden sm:inline">AI Experts Trading System</span>
          </a>

          {/* 우: Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => selectTab(tab.id)}
                className={`px-3 py-1 rounded text-sm transition ${
                  activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* 우: Mobile hamburger */}
          <div className="md:hidden relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 text-gray-400 hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="메뉴"
            >
              <span className="text-xl">{menuOpen ? '\u2715' : '\u2630'}</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 min-w-[160px]">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => selectTab(tab.id)}
                    className={`w-full text-left px-4 py-3 text-sm min-h-[44px] transition ${
                      activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="p-3 md:p-4 max-w-[1440px] mx-auto">
        {/* 상태 바: Live · KR/US · 시간 */}
        <div className="flex items-center gap-2 mb-3 text-xs">
          <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-gray-500">{connected ? 'Live' : 'Off'}</span>
          <span className={`px-1.5 py-0.5 rounded ${krOpen ? 'bg-green-900 text-green-300' : 'bg-red-900/50 text-red-400'}`}>
            {krOpen ? 'KR 장중' : 'KR 마감'}
          </span>
          <span className={`px-1.5 py-0.5 rounded ${usOpen ? 'bg-green-900 text-green-300' : 'bg-red-900/50 text-red-400'}`}>
            {usOpen ? 'US 장중' : 'US 마감'}
          </span>
          <span className="text-gray-600 ml-auto">{timeStr}</span>
        </div>
        <Dashboard activeTab={activeTab} />
      </main>
    </div>
  );
}
